import React, { useState } from 'react';
import {
  Stack,
  Text,
  TextField,
  PrimaryButton,
  IconButton,
  Separator,
  ActionButton,
  DefaultButton,
  Panel,
  PanelType,
  DetailsList,
  SelectionMode,
  MessageBarType,
  MessageBar,
  Modal,
  FontIcon,
  Depths,
  Spinner,
  SpinnerSize,
  ProgressIndicator
} from '@fluentui/react';
import axios from 'axios';

import { styles } from './ProposalDashboard';
import ChatInterface from '../components/ChatInterface';

function buildFileScoreIndex(chunks, options = {}) {
  const {
    scoreField = "score",      // change to "similarity_score" if that's what you have
    topK = 3,
    alpha = 0.2,
    fileIdField = "file_id",
    filenameField = "filename", // if you already have it on chunks; otherwise you can map later
  } = options;

  // Group scores by file_id
  const byFile = new Map();

  for (const ch of chunks || []) {
    const fileId = ch?.[fileIdField];
    if (!fileId) continue;

    const s = Number(ch?.[scoreField]);
    if (!Number.isFinite(s)) continue;

    if (!byFile.has(fileId)) {
      byFile.set(fileId, {
        file_id: fileId,
        filename: ch?.[filenameField], // may be undefined; ok
        scores: [],
      });
    }
    const entry = byFile.get(fileId);
    entry.scores.push(s);

    // keep a filename if we find one later
    if (!entry.filename && ch?.[filenameField]) entry.filename = ch[filenameField];
  }

  // Compute file-level score_index
  const files = [];
  for (const entry of byFile.values()) {
    const sorted = entry.scores.slice().sort((a, b) => b - a);
    const n = sorted.length;

    const top = sorted.slice(0, Math.min(topK, n));
    const topAvg = top.reduce((sum, v) => sum + v, 0) / top.length;

    const coverage = 1 + alpha * Math.log(1 + n);
    const scoreIndex = topAvg * coverage;

    files.push({
      file_id: entry.file_id,
      filename: entry.filename || entry.file_id, // fallback
      chunk_count: n,
      top_scores: top.map(v => Math.round(v * 1000) / 1000),
      top_avg: Math.round(topAvg * 1000) / 1000,
      score_index: Math.round(scoreIndex * 1000) / 1000,
    });
  }

  // Sort: highest score_index first
  files.sort((a, b) => b.top_avg - a.top_avg);

  return files;
}


const RagAssistant = (props) => {
  const { id } = props.match.params;
  const [vectorStore, setVectorStore] = useState(null);
  const [query, setQuery] = useState('');
  const [chunks, setChunks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [showManageStore, setShowManageStore] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null); // { type, text }
  const [results, setResults] = useState(null); // API response.results
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  const fileInputRef = React.useRef(null);

  const pickFiles = () => fileInputRef.current?.click();

  const onFileInputChange = async (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
  };

  const upload = async () => {
    if (!selectedFiles.length) {
      setStatus({ type: MessageBarType.warning, text: "Pick one or more files first." });
      return;
    }

    setUploading(true);
    // setStatus({ type: MessageBarType.info, text: "Uploading files..." });

    try {
      const form = new FormData();
      selectedFiles.forEach((f) => form.append("files", f)); // IMPORTANT: field name "files"

      const resp = await axios.post(`/api/vector-store/${id}/upload-many`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const apiResults = resp.data?.results || [];
      setResults(apiResults);

      const okCount = apiResults.filter((r) => r.ok).length;
      const failCount = apiResults.length - okCount;

      setStatus({
        type: failCount ? MessageBarType.warning : MessageBarType.success,
        text: `Upload complete. Success: ${okCount}, Failed: ${failCount}`,
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Upload failed";
      setStatus({ type: MessageBarType.error, text: msg });
    } finally {
      setUploading(false);
      setSelectedFiles([]);
      fetchVectorStore();
    }
  };

  React.useEffect(async () => {
    fetchVectorStore();
  }, [id]);

  const fetchVectorStore = async () => {
    setLoading(true);
    try {
      const store = await axios.get(`/api/vector-store/${id}`);
      setVectorStore(store.data.store);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching vector store:', error);
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!query.trim()) return;

    setLoading(true);
    try {
      const response = await axios.post(`/api/search/${id}`, {
        query: query
      });

      setChunks(response.data.data.data || []);

    } catch (error) {
      console.error('Search error:', error);
      setChunks([]);
    } finally {
      setLoading(false);
    }
  };

  const getRelevanceColor = (score) => {
    if (score >= 0.8) return '#107c10'; // Green
    if (score >= 0.6) return '#ff8c00'; // Orange
    if (score >= 0.4) return '#e81828'; // Red
    return '#8a8886'; // Gray
  };

  const getRelevanceGrade = (score) => {
    if (score >= 0.8) return 'Highly Relevant';
    if (score >= 0.6) return 'Moderately Relevant';
    if (score >= 0.4) return 'Low Relevance';
    return 'Minimal Relevance';
  };

  const toggleExpanded = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const toggleChunkSelected = (chunk) => {
    const updatedChunks = chunks.map((c) => {
      if (c === chunk) { 
        return { ...c, selected: !c.selected };
      }
      return c;
    });
    setChunks(updatedChunks);
  };

  const copySelectedToClipboard = () => {
    const selectedChunks = chunks.filter((c) => c.selected);
    const combinedText = selectedChunks.map((c) => c.content[0].text).join('\n\n');
    navigator.clipboard.writeText(combinedText).then(() => {
      alert('Selected chunks copied to clipboard!');
    });
  }

  const onDeleteFile = async (vectorStoreId, fileId) => {
    if (!window.confirm("Remove from vector store AND delete the OpenAI file?")) return;

    try {
      const result = await axios.delete(`/api/vector-store/${vectorStoreId}/file/${fileId}`);

      console.log("Delete result:", result);

      if (!result.data.success) {
        console.warn("Delete completed with warnings:", result.data.warnings || result.data.error);
      } else {
        setVectorStore(prev => ({
          ...prev,
          files: prev.files.filter(f => f.id !== fileId)}
        ));
      }
    } catch (e) {
      console.error(e?.response?.data || e.message);
    }
  };


  const filesRelevancyIndexFromChunks = buildFileScoreIndex(chunks, {});

  const columns = [
    { key: 'filename', name: 'File Name', fieldName: 'filename', minWidth: 200, isResizable: true },
    { key: 'status', name: 'Status', fieldName: 'status', minWidth: 100, isResizable: true },
    { key: 'delete', minWidth: 32, isResizable: false, onRender: (item) => {
      return <IconButton iconProps={{iconName: "Delete"}} onClick={() => onDeleteFile(item.vector_store_id, item.id)} />
    }},
  ]

  if(!vectorStore) return <Stack verticalAlign='center' horizontalAlign='center' styles={{root: {height: '500px'}}}>
    <Spinner size={SpinnerSize.large} label="Loading Vector Store..." />
  </Stack>;


  return (
    <Stack styles={{ root: styles.root }} tokens={{ childrenGap: 16 }}>
      {/* Show the manage store dialog */}
      {showManageStore && (
        <Panel
          headerText="Manage Files"
          isOpen={showManageStore}
          type={PanelType.large}
          onDismiss={() => setShowManageStore(false)}
        >
          <ActionButton iconProps={{iconName: "Cancel"}} styles={{ root: { position: 'absolute', top: 10, right: 10 } }} onClick={() => setShowManageStore(false)} />
          <Separator />
          {status && (
            <MessageBar messageBarType={status.type} isMultiline={false} onDismiss={() => setStatus(null)}>
              {status.text}
            </MessageBar>
          )}
          <Stack tokens={{ childrenGap: 12 }}>
            <DefaultButton styles={{root: {width: 'max-content'}}} iconProps={{iconName: "AddTo"}} text="Add Files" onClick={pickFiles} disabled={uploading} />
            {selectedFiles.length > 0 && <>
              <DetailsList 
                items={selectedFiles}
                compact 
                selectionMode={SelectionMode.none} 
                columns={[
                  { key: 'name', name: 'File Name', fieldName: 'name', minWidth: 200, isResizable: true },
                  { key: 'remove', name: '', fieldName: 'remove', minWidth: 32, onRender: (item) => {
                    if (uploading) {
                      return <Spinner size={SpinnerSize.small} />;
                    }
                    return <IconButton disabled={uploading} iconProps={{iconName: "Delete"}} onClick={() => {
                      const updated = selectedFiles.filter((f) => f !== item);
                      setSelectedFiles(updated);
                    }} />
                  }},
                ]} /> 
            </>
            }
            {selectedFiles.length > 0 && <PrimaryButton styles={{root: {width: 'max-content'}}} disabled={uploading} text="Upload Selected Files" onClick={upload} />}
            
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.pdf,.doc,.docx,.md,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              multiple
              style={{ display: "none" }}
              onChange={onFileInputChange}
            />

            { selectedFiles.length > 0 && <Separator /> }

            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 16 }}>
              <Stack.Item grow={1}>
                <Text variant="mediumPlus" style={{fontWeight: 600}}>Files Attached</Text>
              </Stack.Item>
              <Stack horizontal styles={{ root: { marginLeft: 32 } }} tokens={{ childrenGap: 16 }}>
                <Text variant="small" styles={{ root: { color: '#605e5c' } }}>In Progress: {vectorStore.file_counts.in_progress}</Text>
                <Text variant="small" styles={{ root: { color: '#107c10', marginLeft: 16 } }}>Completed: {vectorStore.file_counts.completed}</Text>
                <Text variant="small" styles={{ root: { color: '#ff8c00', marginLeft: 16 } }}>Cancelled: {vectorStore.file_counts.cancelled}</Text>
                <Text variant="small" styles={{ root: { color: '#e81828', marginLeft: 16 } }}>Failed: {vectorStore.file_counts.failed}</Text>
                <Text variant="small" styles={{ root: { marginLeft: 16 } }}>Total Files: {vectorStore.file_counts.total}</Text>
              </Stack>
            </Stack>
            <DetailsList compact selectionMode={SelectionMode.none} items={vectorStore.files} columns={columns}></DetailsList>
          </Stack>
        </Panel>
      )}

      <VectorStoreHowItWorksModal isOpen={showHowItWorks} onDismiss={() => setShowHowItWorks(false)} />


      {/* Search Input Section */}
      <Stack horizontal horizontalAlign='space-between' verticalAlign="center">
        <Text styles={{ root: styles.title }}>
          <FontIcon iconName="ChatBot" style={{ marginRight: 16 }} />
          RAG Assistant for "{vectorStore.name}"
        </Text>
        <Stack horizontal tokens={{ childrenGap: 10 }}>
          <DefaultButton text="How It Works" iconProps={{iconName: "Info"}} onClick={() => setShowHowItWorks(true)} />
          <DefaultButton text="Manage Files" iconProps={{iconName: "Documentation"}} onClick={() => setShowManageStore(true)} />
        </Stack>
      </Stack>
      <Text styles={{ root: styles.subTitle }}>
        Ask questions about your documents stored in this vector store. I will search the contents and return relevant information. From there, you can use the information to help draft responses to RFP questions or other content. I will not answer questions unrelated to the contents of your vector store.
      </Text>
      <Text styles={{root: styles.subTitle}}>
        Select the most relevant chunks from the search results to include in your prompt for best results by checking them. You can then copy the selected chunks to your clipboard to use in external tools, or query directly with the selected content.
      </Text>
      <Separator />
      <Text variant='xLarge'>Ask a Question</Text>
      <Stack horizontal styles={{root: styles.sectionCard}} tokens={{ childrenGap: 10 }}>
        <TextField
          value={query}
          autoComplete='off'
          onChange={(e, val) => setQuery(val || '')}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Ask a question about your vector store contents..."
          styles={{ root: { flex: 1 } }}
        />
        <PrimaryButton
          text={loading ? 'Searching...' : 'Search'}
          onClick={handleSearch}
          disabled={loading}
        />
      </Stack>

      <Separator />

      {/* Results Section */}
      {chunks && chunks.length > 0 ? (
        <Stack tokens={{ childrenGap: 16 }}>
          {/* Header with Result Count */}


          {/* Chunks List */}
          <Stack tokens={{ childrenGap: 12 }}>
            <Stack tokens={{ childrenGap: 12 }}>
              <Text variant="xLarge">
                Review Search Results
              </Text>
              <Stack horizontal tokens={{ childrenGap: 4 }} wrap verticalAlign="center">
              {filesRelevancyIndexFromChunks.map((file, index) => {
                const fileRelevance = file.top_avg;
                const fileScore = Math.round((fileRelevance || 0) * 100);
                return (
                  <div onClick={() => window.open(`https://compassrm.sharepoint.com/_layouts/15/sharepoint.aspx?q=${file.filename}&v=search`)} key={index} style={{ position: 'relative', padding: '2px 8px', borderRadius: '4px', border: '1px solid #e1dfdd', cursor: 'pointer' }} onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                    e.currentTarget.style.borderColor = '#605e5c';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#e1dfdd';
                  }}>
                    <Text variant="small" styles={{ root: { marginBottom: 4 } }}>
                      {file.filename}
                    </Text>
                    <ProgressIndicator styles={{
                      itemProgress: {padding: 0}, 
                      progressBar: {backgroundColor: getRelevanceColor(fileRelevance)},
                      root: {position: 'absolute', bottom: 0, left: 0, width: '100%', padding: 0}}} percentComplete={fileScore / 100} />
                  </div>
                );
              })}
              </Stack>
              {/* {
                chunks.some((c) => c.selected) && (
                  <>
                    <PrimaryButton text="Copy Selected to Clipboard" onClick={copySelectedToClipboard} />
                  </>
                )
              } */}
            </Stack>
            <Stack tokens={{childrenGap: 8}}>
            {chunks.map((chunk, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #e1dfdd',
                  borderRadius: '4px',
                  padding: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: chunk.selected ? '#f3f2f1' : 'white',
                }}
                // onClick={() => toggleExpanded(index)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.borderColor = '#605e5c';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = '#e1dfdd';
                }}
              >
                {/* Chunk Header */}
                <Stack
                  horizontal
                  verticalAlign="center"
                  tokens={{ childrenGap: 8 }}
                >
                  {/* Document Info */}
                  <Stack horizontal styles={{ root: { flex: 1 } }} verticalAlign='center' tokens={{childrenGap: 12}}>
                    <FontIcon iconName={chunk.selected ? "CheckboxCompositeReversed" : "CheckboxComposite"} style={{ fontSize: 20, color: chunk.selected ? '#107c10' : '#605e5c' }} onClick={() => toggleChunkSelected(chunk)} />
                    <Text variant="medium" weight="semibold">
                      {chunk.filename || 'Unknown Document'}
                      <FontIcon iconName="Link" style={{ marginLeft: 8 }} onClick={() => {
                        window.open(`https://compassrm.sharepoint.com/_layouts/15/sharepoint.aspx?q=${chunk.filename}&v=search`)
                      }} />
                    </Text>
                  </Stack>

                  {/* Relevance Indicator */}
                  <Stack
                    horizontal
                    verticalAlign="center"
                    tokens={{ childrenGap: 12 }}
                  >
                    
                    <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                      {getRelevanceGrade(chunk.score || 0)}
                    </Text>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: getRelevanceColor(chunk.score || 0),
                        color: 'white',
                        // fontWeight: 'bold',
                        fontSize: '12px',
                      }}
                    >
                      {Math.round((chunk.score || 0) * 100)}
                    </div>
                  </Stack>

                  {/* Expand Button */}
                  <IconButton
                    onClick={() => toggleExpanded(index)}
                    iconProps={{
                      iconName: expandedIndex === index ? 'ChevronUp' : 'ChevronDown',
                    }}
                  />
                </Stack>

                {/* Expanded Content */}
                {expandedIndex === index && (
                  <Stack tokens={{ childrenGap: 12 }}>
                    <TextField value={chunk.content[0].text} multiline autoAdjustHeight borderless readOnly />
                    <Stack tokens={{ childrenGap: 8 }}>
                      <div
                        style={{
                          backgroundColor: '#f3f2f1',
                          padding: '8px 12px',
                          borderRadius: '4px',
                        }}
                      >
                        <Text variant="small">
                          <strong>Document:</strong> {chunk.filename}
                        </Text>
                        <IconButton iconProps={{iconName: "Link"}} onClick={() => {
                          // getFileContent(chunk.file_id);
                          window.open(`https://compassrm.sharepoint.com/_layouts/15/sharepoint.aspx?q=${chunk.filename}&v=search`)
                          }} />
                      </div>
                      <div
                        style={{
                          backgroundColor: '#f3f2f1',
                          padding: '8px 12px',
                          borderRadius: '4px',
                        }}
                      >
                        <Text variant="small">
                          <strong>File ID:</strong> {chunk.file_id}
                        </Text>
                      </div>
                      <div
                        style={{
                          backgroundColor: '#f3f2f1',
                          padding: '8px 12px',
                          borderRadius: '4px',
                        }}
                      >
                        <Text variant="small">
                          <strong>Relevance Score:</strong>{' '}
                          {Math.round((chunk.score || 0) * 100)}%
                        </Text>
                      </div>
                    </Stack>
                  </Stack>
                )}
              </div>
            ))}
            </Stack>
          </Stack>

          <Separator />

          {/* Chat Interface */}
          <Stack tokens={{ childrenGap: 12 }}>
            <ChatInterface chatTitle="Chat with Selected Results" selectedChunks={chunks.filter(c => c.selected)} />
          </Stack>
        </Stack>
      ) : chunks && chunks.length === 0 ? (
        <Text styles={{ root: { color: '#a4373a' } }}>
          No results found. Try a different query.
        </Text>
      ) : null}
    </Stack>
  );
};

export default RagAssistant;


const VectorStoreHowItWorksModal = (props) => {
  
  const { isOpen, onDismiss } = props;

  const stepsFileUpload = [
    {
      title: "1) Upload file to OpenAI file storage",
      body: "Your backend sends the PDF/DOCX/TXT file to OpenAI. OpenAI stores it.",
    },
    {
      title: "2) Attach file to your vector store",
      body: "The backend attaches the file to your vector store.",
    },
    {
      title: "3) Text is extracted",
      body:"OpenAI reads the document and extracts text (PDF extraction, DOCX paragraphs, TXT as-is).",
    },
    {
      title: "4) Text is chunked",
      body: "The text is split into overlapping chunks (example: ~800 tokens max with ~400 overlap) so context isn't lost at chunk boundaries.",
    },
    {
      title: "5) Each chunk is embedded",
      body: "OpenAI creates an embedding vector (numbers representing meaning) for every chunk and stores them in the vector store",
    },
    {
      title: "6) File becomes searchable",
      body: "When indexing finishes, the vector store file status becomes completed and retrieval can use it.",
    },
  ];

  const stepsSearch = [
    {
      title: "1) User submits a query",
      body: 'Example: "How should we respond to data security requirements in an RFP?"',
    },
    {
      title: "2) Query is embedded",
      body:
        "OpenAI converts the query into an embedding vector in the same embedding space as your stored chunks.",
    },
    {
      title: "3) Similarity search runs",
      body:
        "OpenAI compares the query vector against all chunk vectors and ranks chunks by semantic similarity.",
    },
    {
      title: "4) Matching chunks are returned",
      body:
        "The search endpoint returns top chunks (text/snippet), their file_id, and a relevance score (plus any available metadata).",
    },
    {
      title: "5) Your UI displays results",
      body: "Your frontend renders the chunk list with file names, content and a color/grade based on similarity score.",
    },
  ];

  return (
    <Panel type={PanelType.medium} isOpen={isOpen} onDismiss={onDismiss} isBlocking={false} >
      <Stack
        tokens={{ childrenGap: 16 }}
      >
        {/* Header */}
        <Stack horizontal verticalAlign="center" horizontalAlign="space-between">
          <Text variant="xLarge">How Vector Store Upload + Search Works</Text>
        </Stack>

        <Text variant="medium" styles={{ root: { color: "#605e5c" } }}>
          Sequential flow from uploading a file → chunking/embeddings → retrieving chunks at query time.
        </Text>

        <Separator />

        {/* Upload/Indexing */}
        <Stack tokens={{ childrenGap: 16 }}>
          <Text variant="xLarge">When a file is uploaded</Text>

          {stepsFileUpload.map((s) => (
            <Stack key={s.title}>
              <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>
                {s.title}
              </Text>
              <p>
                {s.body}
              </p>
            </Stack>
          ))}
        </Stack>

        <Separator />

        {/* Query/Retrieval */}
        <Stack tokens={{ childrenGap: 16 }}>
          <Text variant="xLarge">When a user searches</Text>

          {stepsSearch.map((s) => (
            <Stack key={s.title}>
              <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>
                {s.title}
              </Text>
              <p>
                {s.body}
              </p>
            </Stack>
          ))}
        </Stack>

        <Separator />

        {/* Footer */}
        <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 8 }}>
          <PrimaryButton text="Got it" onClick={onDismiss} />
        </Stack>
      </Stack>
    </Panel>
  );
}
